/* eslint-disable react-hooks/exhaustive-deps */
import { ButtonERP, DataTablePrimeReact } from "@finanedi.es/storybook"
import { faTrashCan } from "@fortawesome/pro-light-svg-icons"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { RemesaReadingFilteredDtoProps } from "api/DTOs/RemesaReadingFilteredDto"
import {
  GetRemesasByFiltersParamsProps,
  getRemesasByFilters,
} from "api/Remesa.service"
import { TipoFlujoTesoreriaType } from "api/Types/TipoFlujoTesoreria"
import { AntdPaginationProps } from "api/interfaces/AntdPagination"
import { SortingCriteria } from "api/interfaces/SortingCriteria"
import axios from "axios"
import ActionToast from "common/components/ActionToast"
import DeleteModal from "common/components/Modal/DeleteModal"
import { IconDeletetypes } from "common/components/Modal/DeleteModal/constants"
import { SEARCH_PARAMS } from "common/constants"
import { useDownloadFileContext } from "common/context/DownloadFileProvider"
import TesoreriaService from "common/services/TesoreriaService"
import notification from "common/utils/notification"
import {
  extractFilename,
  getSearchParams,
  requestHandler,
} from "common/utils/utils"
import NewSimpleRemesaModal from "features/Tesoreria/components/NewSimpleRemesaModal"
import { useFilterMenu } from "hooks/useFilterMenu"
import { useCallback, useEffect, useMemo, useRef, useState } from "react"
import { useTranslation } from "react-i18next"
import { useLocation } from "react-router-dom"
import { getIdsFromDocumentos } from "../Cartera/constants"
import { FilterMenu } from "./components/FilterMenu"
import { TableRemesas } from "./components/TableRemesas"
import { DOWNLOAD_REMESA_SEPA_SETUP, DOWNLOAD_SUCCESS_MSG } from "./constants"
import { TABLE_REMESAS_COLUMNS } from "./constants/tableRemesasColumns"

const Remesas = () => {
  const { t } = useTranslation()
  const location = useLocation()
  const param = getSearchParams(location, SEARCH_PARAMS.GOTO)

  const [showDeleteModal, setShowDeleteModal] = useState(false)
  const [createRemesaModalData, setCreateRemesaModalData] = useState<{
    tipo?: TipoFlujoTesoreriaType
    open: boolean
  }>({
    tipo: undefined,
    open: false,
  })
  const [sortingCriteria, setSortingCriteria] = useState<SortingCriteria>({
    sort: undefined,
    property: undefined,
  })
  const [pagination, setPagination] = useState<AntdPaginationProps>({
    itemsPerPage: 100,
    skipCount: 0,
  })
  const [sortField, setSortField] = useState<string | undefined>(undefined)
  const [sortOrder, setSortOrder] = useState<1 | 0 | -1 | null | undefined>(0)
  const [selection, setSelection] = useState<RemesaReadingFilteredDtoProps[]>(
    []
  )
  const dtRef =
    useRef<DataTablePrimeReact<RemesaReadingFilteredDtoProps[]>>(null)
  const handleResetPagination = () => {
    dtRef.current && dtRef.current?.resetScroll()
    setPagination({
      itemsPerPage: pagination.itemsPerPage,
      skipCount: 0,
    })
  }

  const remesasByFiltersCall = useRef(axios.CancelToken.source())
  const fetchRemesas = useCallback(
    () => (queryParams: GetRemesasByFiltersParamsProps) => {
      remesasByFiltersCall.current?.cancel()
      remesasByFiltersCall.current = axios.CancelToken.source()
      return getRemesasByFilters(
        {
          ...queryParams,
          SkipCount: queryParams.SkipCount ?? pagination.skipCount,
          SortingCriteria: queryParams.SortingCriteria,
          MaxResultCount: queryParams.MaxResultCount ?? pagination.itemsPerPage,
        },
        remesasByFiltersCall.current.token
      )
    },
    []
  )

  const { items, isLoading, totalCount, setServiceQuery, fetchTableData } =
    useFilterMenu({
      serviceCall: fetchRemesas,
    })

  const { handleDirectDownloadExcel } = useDownloadFileContext()

  const handleHideToast = () => {
    setSelection([])
  }
  const onRenderContent = useMemo(() => {
    return (
      <div className='tw-flex tw-justify-center md:tw-justify-start'>
        <span className='tw-font-medium'>
          {t("remesas.selectedButton")} {selection?.length}
        </span>
      </div>
    )
  }, [selection?.length])

  const handleOpenDeleteModal = () => {
    setShowDeleteModal(!showDeleteModal)
  }
  const onPostDelete = () => {
    handleOpenDeleteModal()
    handleHideToast()
  }

  const handleBulkDelete = async () => {
    const documentsToDelete = getIdsFromDocumentos(selection)
    requestHandler(
      documentsToDelete,
      TesoreriaService.deleteRemesas,
      fetchTableData,
      `${t("remesas.msg.deleteRemittance")}`,
      null,
      null,
      onPostDelete
    )
  }

  const handleDownloadRemesa = useCallback(
    async (rowData: RemesaReadingFilteredDtoProps) => {
      const { id, tipoRemesable } = rowData
      if (tipoRemesable === undefined) return
      const downloadCallback =
        DOWNLOAD_REMESA_SEPA_SETUP[tipoRemesable]?.downloadDocument

      if (!id || !downloadCallback) return

      try {
        const response = await downloadCallback(id)
        const fileName = extractFilename(
          response.headers["content-disposition"]
        )

        handleDirectDownloadExcel(response, fileName)

        notification.success({
          body: DOWNLOAD_SUCCESS_MSG,
        })
      } catch (error) {
        // eslint-disable-next-line no-console
        console.log(error)
      }
    },
    [handleDirectDownloadExcel]
  )

  const handleOpenCreateRemesaModal = (
    tipo: TipoFlujoTesoreriaType,
    open: boolean
  ) => {
    setCreateRemesaModalData({ tipo, open })
  }

  const openCreateDialogByDefault = () => {
    const isCreateRemesaCobro = param === SEARCH_PARAMS.REMESAS_COBRO
    const isCreateRemesaPago = param === SEARCH_PARAMS.REMESAS_PAGO
    const isCreateDirectAccess = isCreateRemesaCobro || isCreateRemesaPago
    if (isCreateDirectAccess)
      handleOpenCreateRemesaModal(isCreateRemesaCobro ? 1 : 2, true)
  }

  useEffect(() => {
    openCreateDialogByDefault()
  }, [param])

  const onSort = ({
    sortField,
    sortOrder,
  }: {
    sortField: string | undefined
    sortOrder: 1 | 0 | -1 | null | undefined
  }) => {
    setSortingCriteria({
      property: sortField ?? undefined,
      sort: sortOrder === 1 ? "ASC" : sortOrder === -1 ? "DESC" : undefined,
    })
    setSortField(sortField)
    setSortOrder(sortOrder)
    handleResetPagination()
  }

  return (
    <section className='tw-bg-white tw-h-full tw-overflow-hidden'>
      <FilterMenu
        serviceQuery={setServiceQuery}
        sortingCriteria={sortingCriteria}
        maxResultCount={pagination.itemsPerPage}
        skipCount={pagination.skipCount}
        resetPagination={handleResetPagination}
        openCreateRemesaModal={(type: TipoFlujoTesoreriaType) =>
          handleOpenCreateRemesaModal(type, true)
        }
      />
      <div className='tw-h-[calc(100%-80px)]'>
        <TableRemesas
          columns={TABLE_REMESAS_COLUMNS(t, handleDownloadRemesa)}
          dtRef={dtRef}
          items={items}
          loading={isLoading}
          totalCount={totalCount}
          pagination={pagination}
          setPagination={setPagination}
          sortField={sortField}
          sortOrder={sortOrder}
          selection={selection}
          setSelection={setSelection}
          onSort={onSort}
        />
      </div>

      <ActionToast
        show={Boolean(selection?.length)}
        onHide={handleHideToast}
        content={onRenderContent}
        customButtons={
          <ButtonERP
            icon={<FontAwesomeIcon icon={faTrashCan} />}
            onClick={handleOpenDeleteModal}
            label={`${t("remesas.deleteButton")}`}
            customize={{
              type: "danger",
              size: "md",
            }}
          />
        }
      />
      <DeleteModal
        visible={showDeleteModal}
        onHide={handleOpenDeleteModal}
        size='small'
        iconType={IconDeletetypes.remesas}
        buttonLabel={t("remesas.delete.confirmButton")}
        title={t("remesas.delete.deleteRemittance")}
        message={t("remesas.delete.questionRemittance")}
        onConfirm={handleBulkDelete}
      />
      <NewSimpleRemesaModal
        open={createRemesaModalData.open}
        handleOpen={(openStatus) =>
          setCreateRemesaModalData({
            ...createRemesaModalData,
            open: openStatus,
          })
        }
        tipoRemesa={createRemesaModalData?.tipo}
        fetchRemesas={fetchTableData}
      />
    </section>
  )
}

export default Remesas
